"use client";

import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";
import { HttpErrorInfo } from "./types";
import { sanitizePayload } from "@/utils/sanitizePayload";

if (process.env.NEXT_PUBLIC_ENV === "prod") {
  // Initialize Datadog RUM
  datadogRum.init({
    applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
    site: process.env.NEXT_PUBLIC_SITE,
    service: process.env.NEXT_PUBLIC_SERVICE,
    env: "production",
    version: process.env.NEXT_PUBLIC_RELEASE_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    startSessionReplayRecordingManually: true,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    // allowedTracingUrls: [process.env.NEXT_PUBLIC_API_BASE_URL]
  });

  datadogRum.startSessionReplayRecording();

  // Initialize Datadog logs SDK
  datadogLogs.init({
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN || "",
    site: process.env.NEXT_PUBLIC_SITE || "datadoghq.eu",
    service: process.env.NEXT_PUBLIC_SERVICE || "qr-site",
    env: "production",
    version: process.env.NEXT_PUBLIC_RELEASE_VERSION,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ["error"],
    sessionSampleRate: 100,
  });
}

export default function DatadogInit() {
  // doc: https://docs.datadoghq.com/real_user_monitoring/guide/monitor-your-nextjs-app-with-rum/?tab=npm
  // Render nothing - this component is only included so that the init code
  // above will run client-side
  return null;
}

/**
 * Centralized logger for HTTP errors
 * Capture the complete context of failed requests
 */
export const logHttpError = (errorInfo: HttpErrorInfo): void => {
  if (process.env.NEXT_PUBLIC_ENV !== "prod") {
    //Error info in console in development
    console.error("[HTTP Error]", errorInfo);
    return;
  }

  try {
    const {
      message,
      statusCode,
      endpoint,
      method = "UNKNOWN",
      payload,
      response,
      timestamp = new Date().toISOString(),
      userContext,
    } = errorInfo;

    // Send structured log to Datadog
    datadogLogs.logger.error(message, {
      http: {
        status_code: statusCode,
        url: endpoint,
        method,
      },
      error: {
        message,
        timestamp,
      },
      request: {
        payload: sanitizePayload(payload),
      },
      response: {
        data: sanitizePayload(response),
      },
      user: sanitizePayload(userContext),
    });
  } catch (error) {
    console.error("[Datadog Logger Error]", error);
  }
};

/**
 * Logger for application errors
 */
export const logError = (error: Error, context?: Record<string, unknown>): void => {
  if (process.env.NEXT_PUBLIC_ENV !== "prod") {
    console.error("[App Error]", error, context);
    return;
  }

  try {
    datadogLogs.logger.error(error.message, {
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name,
      },
      context: sanitizePayload(context),
    });
  } catch (err) {
    console.error("[Datadog Logger Error]", err);
  }
};

/**
 * Logger for general information
 */
export const logInfo = (message: string, context?: Record<string, unknown>): void => {
  if (process.env.NEXT_PUBLIC_ENV !== "prod") {
    console.info("[Info]", message, context);
    return;
  }

  try {
    const sanitized = sanitizePayload(context);
    datadogLogs.logger.info(message, (sanitized as Record<string, unknown>) || {});
  } catch (error) {
    console.error("[Datadog Logger Error]", error);
  }
};

/**
 * Logger for warnings
 */
export const logWarning = (message: string, context?: Record<string, unknown>): void => {
  if (process.env.NEXT_PUBLIC_ENV !== "prod") {
    console.warn("[Warning]", message, context);
    return;
  }

  try {
    const sanitized = sanitizePayload(context);
    datadogLogs.logger.warn(message, (sanitized as Record<string, unknown>) || {});
  } catch (error) {
    console.error("[Datadog Logger Error]", error);
  }
};
